import BaseEntityType from '@enums/BaseEntityType';
import { EntityPlural, EntityPluralLower } from '@enums/EntityName';
import EntityType from '@enums/EntityType';
import { NO_PROCESS } from '@utils/entityMaps/resolveActivityProcess';
import unassignedPerson from '@utils/people/unassignedPerson';
import untaggedTag from '@utils/tags/untaggedTag';

export default function retrieveSubscribedItem({
  id,
  parentId,
  type,
  library,
  entities,
}) {
  if (!id || !type || !library || !entities) {
    return;
  }

  const entityType = BaseEntityType[type];
  const libraryTags = library?.tags;

  switch (type) {
    case EntityType.ACTIVITY:
    case EntityType.GROUP:
    case EntityType.ROLE:
      const entityName = EntityPlural[type].toLowerCase();
      return entities[entityName]?.[id];
    case EntityType.PERSON:
      if (id === EntityType.UNASSIGNED) {
        return unassignedPerson();
      }
      const entityPlural = EntityPlural[type].toLowerCase();
      return entities[entityPlural]?.[id];
    case EntityType.ACTIVITY_TAG:
    case EntityType.ROLE_TAG:
    case EntityType.GROUP_TAG:
      if (id === EntityType.UNTAGGED) {
        return untaggedTag();
      }
      return libraryTags?.tags?.map?.get(id);
    case EntityType.ACTIVITY_TAG_CATEGORY:
    case EntityType.ROLE_TAG_CATEGORY:
    case EntityType.GROUP_TAG_CATEGORY:
      const categoryTag = libraryTags?.categories?.map?.get(id);

      if (categoryTag && categoryTag.__taggables_set.has(entityType)) {
        return categoryTag;
      }

      const tag = { uuid: id };

      if (id === 'system' && entityType === EntityType.ACTIVITY) {
        tag.is_system = true;
        tag.name = 'Beamible';
        tag.color = 'YELLOW_REGULAR';

        return tag;
      }
      if (
        id === 'primary' &&
        libraryTags?.ordered.custom.primary.taggables.has(entityType)
      ) {
        tag.is_system = true;
        tag.name = 'Primary';
        tag.color = 'BLUE_REGULAR';

        return tag;
      }
      return;
    case EntityType.ACTIVITY_TYPE:
      return library?.activities?.mapped?.get(id);
    case EntityType.ACTIVITY_PROPERTY:
    case EntityType.ROLE_PROPERTY:
    case EntityType.GROUP_PROPERTY:
      const property = library?.properties?.mapped?.get(id);
      return property;
    case EntityType.ACTIVITY_PROPERTY_VALUE:
    case EntityType.GROUP_PROPERTY_VALUE:
    case EntityType.ROLE_PROPERTY_VALUE:
      const valueProperty = library?.properties?.mapped?.get(parentId);
      if (!valueProperty) {
        return;
      }
      const propertyValue = valueProperty.__options.get(id);
      return {
        ...propertyValue,
        color: valueProperty.color,
        __color: valueProperty.__color,
        property: valueProperty,
      };
    case EntityType.ROLE_SKILL:
    case EntityType.PERSON_SKILL:
      const skill = library?.skills?.map?.get(id);
      return skill;
    case EntityType.ROLE_SKILL_LEVEL:
    case EntityType.PERSON_SKILL_LEVEL:
      const levelSkill = library?.skills?.map?.get(parentId);
      return { ...levelSkill, level: id };
    case EntityType.ROLE_TYPE:
      const roleHash = library?.roles?.titles.default.get(id);
      const roleType = library?.roles?.map?.get(roleHash);
      if (roleType) {
        return roleType;
      }
      // Role titles that aren't in the design's role library.
      return Object.values(entities.roles ?? {}).some(
        ({ title }) => title === id
      )
        ? { title: id }
        : undefined;
    case EntityType.SPAN:
      return { uuid: id, name: id };
    case EntityType.MANAGER:
      return entities[EntityPluralLower.ROLE]?.[id];
    case EntityType.L1_PROCESS:
      if (id === NO_PROCESS) {
        return {
          uuid: NO_PROCESS,
          name: 'No process',
          color: 'GREY_LIGHT',
        };
      }
      const { level: _l1Level, ...l1 } = library?.processes?.l1?.get(id) || {};
      return l1.uuid ? l1 : undefined;
    case EntityType.PROCESS:
      const rawL2 =
        library?.processes?.l2?.get(id) || library?.processes?.mapped?.get(id);
      if (!rawL2) {
        return;
      }
      const { level: _l2Level, ...l2 } = rawL2;
      if (!l2.color) {
        l2.color = library?.processes?.l1?.get(l2.parent_id)?.color;
      }
      return l2;
    default:
      return;
  }
}
